/* eslint-disable */
import React, { useEffect, useMemo } from "react";
import {
  Button,
  Group,
  TextInput,
  Loader,
  Center,
  NumberInput,
  Select,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { z } from "zod";
import { useParams } from "react-router-dom";

import { useEmploymentHistory } from "api/users/getEmployementHistory";
import { useUpdateEmploymentHistory } from "api/users/hiringDetailUpdate";

type FormValues = {
  startDate?: string | null;
  endDate?: string | null;
  position: string;
  salaryAmount?: number | null;
  salaryType: string;
  currency: string;
  entryId?: string | null;
};

const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

const createHiringSchema = z.object({
  startDate: z
    .string()
    .optional()
    .nullable()
    .refine((v) => !v || dateRegex.test(v), { message: "Invalid start date" }),
  endDate: z
    .string()
    .optional()
    .nullable()
    .refine((v) => !v || dateRegex.test(v), { message: "Invalid end date" }),
  position: z.string().trim().min(1, { message: "Please enter position" }),
  salaryAmount: z
    .number()
    .optional()
    .nullable()
    .refine((v) => v === undefined || v === null || v >= 0, {
      message: "Salary must be >= 0",
    }),
  salaryType: z.string().trim().min(1),
  currency: z.string().trim().min(1),
});

export default function HiringDetailForm({
  onClose,
  entryId,
}: {
  onClose: () => void;
  entryId?: string;
}) {
  const { userid } = useParams();
  const { data: employmenthistory, isLoading: isLoadingHistory } =
    useEmploymentHistory(userid!);
  const hiringDetailUpdateMutation = useUpdateEmploymentHistory();

  const selectedRecord = useMemo(() => {
    const list =
      employmenthistory?.data?.employmentHistory &&
      Array.isArray(employmenthistory.data.employmentHistory)
        ? employmenthistory.data.employmentHistory
        : [];

    if (entryId) {
      return list.find((r: any) => r._id === entryId) ?? null;
    }
    return list.length > 0 ? list[0] : null;
  }, [employmenthistory, entryId]);

  const form = useForm<FormValues>({
    initialValues: {
      startDate: "",
      endDate: "",
      position: "",
      salaryAmount: undefined,
      salaryType: "annual",
      currency: "",
      entryId: entryId ?? null,
    },
    validate: zodResolver(createHiringSchema),
  });

  const toDateInput = (iso?: string | null) => {
    if (!iso) return "";

    return iso.slice(0, 10);
  };

  useEffect(() => {
    if (!selectedRecord) return;

    form.setValues({
      startDate: toDateInput(selectedRecord.startDate),
      endDate: toDateInput(selectedRecord.endDate),
      position: selectedRecord.position || "",
      salaryAmount:
        selectedRecord.salaryAmount === undefined ||
        selectedRecord.salaryAmount === null
          ? undefined
          : Number(selectedRecord.salaryAmount),
      salaryType: selectedRecord.salaryType || "annual",
      currency: selectedRecord.currency || "",
      entryId: selectedRecord._id ?? entryId ?? null,
    });
  }, [selectedRecord]);

  const handleSubmit = (values: FormValues) => {
    if (!userid) {
      showNotification({
        title: "Error",
        message: "Missing user ID — cannot update hiring details.",
        color: "red",
      });
      return;
    }

    const employmentHistoryId: string | null =
      values.entryId ?? entryId ?? (selectedRecord ? selectedRecord._id : null);

    if (!employmentHistoryId) {
      showNotification({
        title: "Error",
        message: "Missing employment entry id — cannot update.",
        color: "red",
      });
      return;
    }

    const payload: any = {
      startDate: values.startDate || undefined,
      position: values.position.trim(),
      salaryAmount:
        values.salaryAmount === undefined || values.salaryAmount === null
          ? 0
          : Number(values.salaryAmount),
      salaryType: values.salaryType || "annual",
      currency: values.currency || "",
    };

    // Only include endDate if it has a value, otherwise explicitly send null to clear it
    if (values.endDate && values.endDate.trim() !== "") {
      payload.endDate = values.endDate;
    } else {
      payload.endDate = null;
    }

    hiringDetailUpdateMutation.mutate(
      { userId: userid, entryId: employmentHistoryId, payload },
      {
        onSuccess: () => {
          showNotification({
            title: "Success",
            message: "Hiring details updated successfully.",
            color: "green",
          });
          onClose();
        },
        onError: (err: any) => {
          const message =
            err?.response?.data?.message ??
            err?.message ??
            "Failed to update hiring details";
          showNotification({ title: "Error", message, color: "red" });
        },
      }
    );
  };

  if (isLoadingHistory) {
    return (
      <Center style={{ padding: 20 }}>
        <Loader />
      </Center>
    );
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <TextInput
        label="Start Date"
        placeholder="YYYY-MM-DD"
        type="date"
        mb={12}
        {...form.getInputProps("startDate")}
      />

      <TextInput
        label="End Date"
        placeholder="YYYY-MM-DD"
        type="date"
        mb={12}
        {...form.getInputProps("endDate")}
      />

      <TextInput
        label="Position"
        withAsterisk
        mb={12}
        {...form.getInputProps("position")}
      />

      <NumberInput
        label="Salary Amount"
        mb={12}
        precision={2}
        step={0.01}
        min={0}
        {...form.getInputProps("salaryAmount")}
      />

      <Select
        label="Salary Type"
        mb={12}
        data={[
          { value: "annual", label: "Annual" },
          { value: "daily", label: "Daily" },
          { value: "hourly", label: "Hourly" },
          { value: "biweekly", label: "Biweekly" },
        ]}
        {...form.getInputProps("salaryType")}
      />

      <Select
        label="Currency"
        placeholder="Select currency"
        searchable
        mb={12}
        data={[
          { value: "PKR", label: "PKR - Pakistani Rupee" },
          { value: "USD", label: "USD - US Dollar" },
          { value: "EUR", label: "EUR - Euro" },
          { value: "GBP", label: "GBP - British Pound" },
          { value: "AED", label: "AED - UAE Dirham" },
          { value: "SAR", label: "SAR - Saudi Riyal" },
          { value: "INR", label: "INR - Indian Rupee" },
          { value: "CAD", label: "CAD - Canadian Dollar" },
          { value: "AUD", label: "AUD - Australian Dollar" },
        ]}
        {...form.getInputProps("currency")}
      />

      <Group position="right" spacing={20} mt={20}>
        <Button color="cancel" onClick={onClose} type="button">
          Cancel
        </Button>
        <Button
          type="submit"
          variant="filled"
          loading={hiringDetailUpdateMutation.isLoading}
        >
          Update Employment History
        </Button>
      </Group>
    </form>
  );
}
